import {
  Avatar,
  Badge,
  BodyText,
  Button,
  Chip,
  Drawer,
  DrawerContentElement,
  Link,
  Menu,
  MenuItem,
  Separator,
  Spinner,
} from '@farol-ds/react'
import { useGA4Dispatcher } from '@jusbrasil-web/shared-ga4'
import {
  COMPONENT_CLASS,
  GA4ClickEvent,
  storeComponentOriginForNextPage,
} from '@jusbrasil-web/shared-ga4-events'
import { pluralize } from '@jusbrasil-web/shared-utils-format'
import { forwardRef, lazy, Suspense, useContext } from 'react'

import { TopbarContext } from '../../context'
import { sendUnifiedClickEvent } from '../../utils'
import { withNextUrl } from '../../utils/next-url'
import { TopbarSkeleton } from '../topbar-skeleton'
import { MenuItemOverrideFn, TopbarGA4Event, useMenuItems, UseMenuItemsMountFn } from './menu-items'
import styles from './topbar-user-menu-content.module.scss'
import { TopbarUserMenuContentProfileSwitch } from './topbar-user-menu-content-profile-switch'
import { TopbarUserMenuTakeover } from './topbar-user-menu-takeover'

const TopbarUserFeatureFlagMenuLazy = lazy(() =>
  import('./topbar-user-feature-flag-menu').then((m) => ({ default: m.TopbarUserFeatureFlagMenu }))
)

const TopbarForceExperimentMenuLazy = lazy(() =>
  import('./topbar-force-experiment-menu').then((m) => ({ default: m.TopbarForceExperimentMenu }))
)

export type TopbarUserMenuContentElement = DrawerContentElement

export interface TopbarUserMenuContentProps {
  mountMenuItems?: UseMenuItemsMountFn
  overrideMenuItem?: MenuItemOverrideFn
}

export const TopbarUserMenuContent = forwardRef<
  TopbarUserMenuContentElement,
  TopbarUserMenuContentProps
>(function TopbarUserMenuContent(props, forwardedRef) {
  const { mountMenuItems, overrideMenuItem } = props

  const ctx = useContext(TopbarContext)
  const menuItems = useMenuItems(ctx, mountMenuItems, overrideMenuItem)
  const { logged, user, ready, currentUrl } = ctx
  const { sendGA4Event } = useGA4Dispatcher()

  const sendGA4ClickEvent = (topbarGA4Event: TopbarGA4Event) => {
    const { componentName, selectedOption } = topbarGA4Event
    sendGA4Event(
      new GA4ClickEvent({
        componentClass: COMPONENT_CLASS.TOPBAR_USER_MENU,
        componentName,
        selectedOption,
        resourceId: null,
      })
    )
  }

  const storeGA4ComponentForNextPage = (topbarGA4Event: TopbarGA4Event) => {
    const { componentName, selectedOption } = topbarGA4Event
    storeComponentOriginForNextPage({
      componentClass: COMPONENT_CLASS.TOPBAR_USER_MENU,
      componentName,
      selectedOption,
    })
  }

  const handleGA4Event = (topbarGA4Event: TopbarGA4Event) => {
    switch (topbarGA4Event.method) {
      case 'store-component-for-next-page':
        storeGA4ComponentForNextPage(topbarGA4Event)
        break
      default:
        sendGA4ClickEvent(topbarGA4Event)
    }
  }

  return (
    <Drawer.Content ref={forwardedRef} data-testid="topbar-header-user-menu-content">
      <Drawer.IconClose />

      <Drawer.Header>
        <Drawer.HeaderTitle>Minha conta</Drawer.HeaderTitle>
      </Drawer.Header>

      <Drawer.Body className={styles.body}>
        {logged && (
          <div className={styles.userInfo}>
            {ready && user && (
              <>
                <Avatar src={user.image || undefined} alt={user.name} size="xl" />
                <div className={styles.userInfoText}>
                  <div className={styles.userInfoNameContainer}>
                    <BodyText size="lg" className={styles.userInfoName} asChild>
                      <strong>{user.name}</strong>
                    </BodyText>
                    {user.isB2BSelfService && (
                      <Chip size="sm" kind="tertiary">
                        Administrador
                      </Chip>
                    )}
                  </div>
                  <Link
                    onClick={() => {
                      sendUnifiedClickEvent('Ver meu perfil', user?.pid)
                    }}
                    type="primary"
                    href={`https://${user.username}.jusbrasil.com.br`}
                    className={styles.userInfoLink}
                  >
                    Acessar meu perfil
                  </Link>
                </div>
              </>
            )}

            {!ready && (
              <>
                <TopbarSkeleton animateBg radius="circle" style={{ height: 48, width: 48 }} />
                <TopbarSkeleton
                  marginLeft="3xs"
                  display="flex"
                  justifyContent="center"
                  flexDirection="column"
                  style={{ height: 51 }}
                >
                  <TopbarSkeleton radius="text" animateBg style={{ width: 160, height: 16 }} />
                  <TopbarSkeleton
                    animateBg
                    radius="text"
                    marginTop="3xs"
                    style={{ width: 124, height: 12 }}
                  />
                </TopbarSkeleton>
              </>
            )}
          </div>
        )}

        {ready && !user && (
          <div className={styles.anonyButtons}>
            <Button kind="secondary" block asChild>
              <a href={withNextUrl('https://www.jusbrasil.com.br/cadastro', currentUrl)}>
                Cadastre-se
              </a>
            </Button>
            <Button block asChild>
              <a href={withNextUrl('https://www.jusbrasil.com.br/login', currentUrl)}>Entrar</a>
            </Button>
          </div>
        )}

        <Menu className={styles.menu}>
          <Separator className={styles.separator} />

          {menuItems.map((item, idx) => {
            if (typeof item === 'string') {
              switch (item) {
                case 'TAKEOVER':
                  return <TopbarUserMenuTakeover key={`takeover-${idx}`} />
                case 'PROFILE_SWITCH':
                  return <TopbarUserMenuContentProfileSwitch key={`profile-switch-${idx}`} />

                case 'SEPARATOR':
                  return <Separator className={styles.separator} key={`sep-${idx}`} />

                default:
                  return null
              }
            }

            return (
              <MenuItem key={item.url} asChild>
                <a
                  href={item.url}
                  target={item.target}
                  onClick={(evt) => {
                    if (item.clickEvents) {
                      item.clickEvents.forEach((clickEvent) => {
                        sendUnifiedClickEvent(clickEvent, user?.pid)
                      })
                    }
                    if (item.ga4Events) {
                      item.ga4Events.forEach((topbarGA4Event) => {
                        handleGA4Event(topbarGA4Event)
                      })
                    }
                    item.onClick?.(evt)
                  }}
                >
                  <MenuItem.Content>
                    {item.leading ? <MenuItem.Leading>{item.leading}</MenuItem.Leading> : null}
                    {item.title}
                    {item.newItem ? (
                      <Badge variant="dot" type="primary" className={styles.newItemBadge} />
                    ) : null}
                    {item.trailing ? <MenuItem.Trailing>{item.trailing}</MenuItem.Trailing> : null}
                    {item.count ? (
                      <MenuItem.Trailing>
                        <Badge
                          type="secondary"
                          count={item.count}
                          aria-label={`${item.count} ${pluralize(
                            item.count,
                            'não lida',
                            'não lidas'
                          )}`}
                        />
                      </MenuItem.Trailing>
                    ) : null}
                  </MenuItem.Content>
                </a>
              </MenuItem>
            )
          })}
        </Menu>

        {user?.admin && (
          <Suspense fallback={<Spinner />}>
            <Separator className={styles.separator} />
            <TopbarUserFeatureFlagMenuLazy />
            <Separator className={styles.separator} />
            <TopbarForceExperimentMenuLazy />
          </Suspense>
        )}
      </Drawer.Body>
    </Drawer.Content>
  )
})
